'use client'

import {useCallback, useEffect, useRef, useState} from 'react'
import {apiGet, apiPost} from '@/lib/api'
import type {Account, Contact} from '@/types'

const POLL_INTERVAL_MS = 2000

type ContactsSyncState = {
    contacts: Contact[]
    syncing: boolean
    syncError: string
    syncContacts: (currentAccount?: Account | null) => Promise<void>
    resolveContact: (wxid: string) => Promise<Contact | null>
    updateContact: (wxid: string, update: Partial<Contact> | ((contact: Contact) => Contact)) => void
    reset: () => void
}

export function useContactsSync(account: Account | null): ContactsSyncState {
    const [contacts, setContacts] = useState<Contact[]>([])
    const [syncing, setSyncing] = useState(false)
    const [syncError, setSyncError] = useState('')
    const resolvingRef = useRef(new Map<string, Promise<Contact | null>>())
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

    // Stop any running poll on unmount.
    useEffect(() => () => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }, [])

    /** Poll GET /contacts until the backend reports syncing: false. */
    const pollUntilDone = useCallback((wxid: string) => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current)
        setSyncing(true)
        const poll = async () => {
            try {
                const res = await apiGet<{
                    success: boolean
                    contacts: Contact[]
                    syncing?: boolean
                }>('/contacts?wxid=' + encodeURIComponent(wxid), {silent: true})
                setContacts(res.contacts || [])
                if (!res.syncing) {
                    if (pollTimerRef.current) {
                        clearInterval(pollTimerRef.current)
                        pollTimerRef.current = null
                    }
                    setSyncing(false)
                }
            } catch {
                // keep polling on transient errors
            }
        }
        void poll()
        pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS)
    }, [])

    const syncContacts = useCallback(
        async (currentAccount: Account | null = account) => {
            if (!currentAccount) return
            setSyncError('')
            setSyncing(true)
            try {
                const response = await apiPost<{
                    success: boolean
                    contacts?: Contact[]
                    syncing?: boolean
                }>(
                    '/contacts',
                    {wxid: currentAccount.wxid},
                )
                if (response.syncing) {
                    pollUntilDone(currentAccount.wxid)
                    return
                }
                setContacts(response.contacts || [])
                setSyncing(false)
            } catch (error) {
                setSyncError(error instanceof Error ? error.message : '通讯录同步失败')
                setSyncing(false)
            }
        },
        [account, pollUntilDone],
    )

    const resolveContact = useCallback(
        async (wxid: string): Promise<Contact | null> => {
            if (!account || !wxid) return null
            if (wxid === account.wxid) return null
            if (wxid.startsWith('gh_')) return null

            const cached = contacts.find((c) => c.wxid === wxid)
            const needsAvatarResolution = wxid.endsWith('@chatroom') || wxid.endsWith('@openim')
            if (cached && (!needsAvatarResolution || cached.avatar)) {
                return cached
            }

            const pending = resolvingRef.current.get(wxid)
            if (pending) return pending

            const promise = (async (): Promise<Contact | null> => {
                try {
                    const response = await apiPost<{
                        success: boolean
                        contacts: Contact[]
                        missing?: string[]
                    }>(
                        '/contacts/resolve',
                        {wxid: account.wxid, wxids: [wxid]},
                        {silent: true},
                    )
                    const resolved = (response.contacts || []).find(
                        (c) => c.wxid === wxid,
                    ) ?? null
                    if (resolved) {
                        setContacts((current) => {
                            const idx = current.findIndex((c) => c.wxid === resolved.wxid)
                            if (idx === -1) return [...current, resolved]
                            const next = current.slice()
                            next[idx] = {...current[idx], ...resolved}
                            return next
                        })
                    }
                    return resolved
                } catch {
                    return null
                } finally {
                    resolvingRef.current.delete(wxid)
                }
            })()
            resolvingRef.current.set(wxid, promise)
            return promise
        },
        [account, contacts],
    )

    const updateContact = useCallback((wxid: string, update: Partial<Contact> | ((contact: Contact) => Contact)) => {
        setContacts((current) => {
            const idx = current.findIndex((contact) => contact.wxid === wxid)
            if (idx === -1) return current
            const next = current.slice()
            const existing = current[idx]
            next[idx] = typeof update === 'function' ? update(existing) : {...existing, ...update}
            return next
        })
    }, [])

    // Load contacts from database. If backend is syncing (e.g. after login),
    // start polling until sync completes.
    useEffect(() => {
        if (!account) return
        void apiGet<{
            success: boolean
            contacts: Contact[]
            syncing?: boolean
        }>('/contacts?wxid=' + encodeURIComponent(account.wxid)).then((res) => {
            setContacts(res.contacts || [])
            if (res.syncing) {
                pollUntilDone(account.wxid)
            }
        })
    }, [account, pollUntilDone])

    const reset = useCallback(() => {
        setContacts([])
        setSyncError('')
        setSyncing(false)
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current)
            pollTimerRef.current = null
        }
        resolvingRef.current.clear()
    }, [])

    return {contacts, syncing, syncError, syncContacts, resolveContact, updateContact, reset}
}
